import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { KeyRound, LogIn } from 'lucide-react';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button, Note, TextInput } from '@/components/ui';
import { RegisterCta } from '@/components/RegisterCta';
import { useAuth } from '@/auth/AuthProvider';
import { ApiError } from '@/lib/api';
import { useT } from '@/i18n/context';
import { rememberDevice, signoutReason } from '@/lib/session';

export function LoginPage() {
  const { signIn, completeMfa, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // «ابقَ مسجّلاً على هذا الجهاز» (0068): الخانةُ تتذكّر اختيارَها على هذا الجهاز، والخادمُ يقرّر لمن تُفتح جلسةٌ بالأيام
  const [remember, setRemember] = useState(() => rememberDevice.get());
  // الخطوةُ الثانية (0064): تحدٍّ من الخادم ورمزٌ من تطبيق المصادقة — أو رمزُ استرداد
  const [mfa, setMfa] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  // لماذا خرجتَ؟ (خمول، مدّة قصوى، جلسةٌ أُنهيت) — يُقال مرةً ثم يُمحى
  const [notice] = useState(() => signoutReason.peek());
  useEffect(() => { signoutReason.take(); }, []);

  // من أين جاء؟ (`Protected` يحفظه) — مسارٌ داخليٌّ وحده، فلا يصير الدخولُ باباً إلى موقعٍ آخر.
  // يُحترم بعد الدخول الصريح أيضاً لا عند فتح الصفحة وحده: اختصارُ الأيقونة `/go/inbox` يمرّ بالدخول
  // ثم يعود إلى نيّته (26 سبتمبر 2026) — كان يعود إلى الرئيسية دائماً.
  const rawFrom = (location.state as { from?: string } | null)?.from;
  const from = typeof rawFrom === 'string' && rawFrom.startsWith('/') && !rawFrom.startsWith('//') && !rawFrom.startsWith('/login') ? rawFrom : '/';
  if (status === 'authenticated') return <Navigate to={from} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      rememberDevice.set(remember);
      const r = await signIn(email.trim(), password, remember);
      if ('mfaRequired' in r) { setMfa(r.mfaToken); setPassword(''); return; }
      navigate(r.mustChangePassword ? '/change-password' : from, { replace: true });
    } catch (err) {
      // رسالة الخادم عربية دائماً (مرجع المستندات)؛ ما نترجمه هو نصّ الواجهة الاحتياطي
      setError(err instanceof ApiError ? err.message : t('login.failed'));
    } finally {
      setBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    if (!mfa) return;
    setError(null);
    setBusy(true);
    try {
      rememberDevice.set(remember);
      const pair = await completeMfa(mfa, useRecovery ? { recoveryCode: code.trim() } : { code: code.replace(/\s+/g, '') }, remember, email.trim());
      navigate(pair.mustChangePassword ? '/change-password' : from, { replace: true });
    } catch (err) {
      const expired = err instanceof ApiError && err.code === 'MFA_EXPIRED';
      if (expired) { setMfa(null); setCode(''); }
      setError(err instanceof ApiError ? err.message : t('login.failed'));
    } finally {
      setBusy(false);
    }
  }

  if (mfa) {
    const codeOk = useRecovery ? code.replace(/[^A-Za-z0-9]/g, '').length === 10 : /^\d{6}$/.test(code.replace(/\s+/g, ''));
    return (
      <AuthLayout title="التحقّق بخطوتين">
        <form className="stack gap-4" onSubmit={(e) => void submitMfa(e)} noValidate>
          {error && <Note kind="danger">{error}</Note>}
          <p className="muted">{useRecovery ? 'اكتب أحد رموز الاسترداد التي حفظتها عند التفعيل — كلُّ رمزٍ يُستعمل مرّةً واحدة.' : 'افتح تطبيق المصادقة على جوالك واكتب الرمز المكوّن من ستة أرقام.'}</p>
          <TextInput
            label={useRecovery ? 'رمز الاسترداد' : 'رمز التطبيق'} name="otp" ltr required autoFocus
            autoComplete={useRecovery ? 'off' : 'one-time-code'} inputMode={useRecovery ? 'text' : 'numeric'} maxLength={useRecovery ? 11 : 7}
            value={code} onChange={(e) => setCode(e.target.value)}
          />
          <label className="checkbox" data-testid="trust-device">
            <input type="checkbox" name="trust" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
            <span>
              <span className="field__label">احفظ هذا الجهاز</span>
              <span className="caption" style={{ display: 'block' }}>لا يُطلب رمزُ التطبيق على هذا الجهاز مدّةً (30 يوماً، و7 لحسابات إدارة المنصة) — كلمةُ المرور تبقى مطلوبة. أزل العلامة على جهازٍ يستعمله غيرك.</span>
            </span>
          </label>
          <Button type="submit" variant="primary" block loading={busy} disabled={!codeOk} disabledReason={!codeOk ? (useRecovery ? 'رمز الاسترداد عشرة محارف.' : 'الرمز ستة أرقام.') : undefined} icon={<KeyRound size={17} />}>
            تحقّق وادخل
          </Button>
          <button type="button" className="link-btn" onClick={() => { setUseRecovery((v) => !v); setCode(''); }}>
            {useRecovery ? 'استعمل رمز التطبيق' : 'لا أصل إلى جوالي — استعمل رمز استرداد'}
          </button>
          <button type="button" className="link-btn" onClick={() => { setMfa(null); setCode(''); setError(null); }}>عودة إلى الدخول</button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('login.title')}>
      <form className="stack gap-4" onSubmit={(e) => void submit(e)} noValidate>
        {notice && !error && <Note kind="info">{notice}</Note>}
        {error && <Note kind="danger">{error}</Note>}

        <TextInput
          label={t('login.email')} type="email" name="email" autoComplete="username" required ltr
          value={email} onChange={(e) => setEmail(e.target.value)}
        />
        <TextInput
          label={t('login.password')} type="password" name="password" autoComplete="current-password" required ltr
          value={password} onChange={(e) => setPassword(e.target.value)}
        />
        {/* الخانةُ معلَّمةٌ افتراضاً (25 أكتوبر 2026): الجهازُ الشخصي يبقى داخلاً فتصله الإشعارات؛ والشرحُ سطرٌ واحد — الصفحةُ كلُّها بلا تمرير */}
        <label className="checkbox login__remember" data-testid="remember-device">
          <input type="checkbox" name="remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          <span>
            <span className="field__label">ابقَ مسجّلاً على هذا الجهاز</span>
            <span className="caption" style={{ display: 'block' }}>أزل العلامة على جهازٍ يستعمله غيرك.</span>
          </span>
        </label>

        <Button type="submit" variant="primary" block loading={busy} icon={<LogIn size={17} />}>
          {t('login.submit')}
        </Button>
        <p className="muted login__foot"><Link to="/forgot-password">{t('auth.forgot')}</Link><span className="caption" title={t('login.lockoutNote')}>خمس محاولات فاشلة تقفل الحساب مؤقتاً.</span></p>
      </form>
      {/* بابٌ واحد للتسجيل يفتح ثلاثةَ خيارات (5 أكتوبر 2026) — بدل أربعة أبوابٍ بأزرارها دفعةً واحدة؛ وضمُّ المركز من الصفحة الأولى لا من هنا */}
      <RegisterCta />
    </AuthLayout>
  );
}
